import type { Role } from './roles';

/**
 * Authority / identity presentation — the SINGLE source of truth for how the
 * seven backend roles are shown in the UI. Mirrors the Executive Authority
 * Model: three derived tiers over the unchanged backend roles. The server
 * remains authoritative; this is presentation/gating only.
 *
 * Rules (binding):
 *  - Exactly three tiers: SUPER_ADMIN > ADMIN > EMPLOYEE.
 *  - The literal internal role strings ("superadmin", "manager", "team_lead",
 *    "bde", "developer", ...) are NEVER rendered in an authority context.
 *  - Owner identity renders as the configured owner name/designation, or the
 *    signed-in owner's own display name. ZYNX is white-labelled, so no
 *    customer's person or company name is ever hardcoded here.
 */

export type AuthorityTier = 'SUPER_ADMIN' | 'ADMIN' | 'EMPLOYEE';

/** Derive the authority tier from a backend role. */
export function tierOf(role: Role | string | null | undefined): AuthorityTier {
  if (role === 'superadmin') return 'SUPER_ADMIN';
  if (role === 'admin' || role === 'department_admin') return 'ADMIN';
  // manager / team_lead / bde / developer / employee are all EMPLOYEE-tier.
  return 'EMPLOYEE';
}

/** True for the executive (owner) tier — gates hidden executive surfaces. */
export function isExecutive(role: Role | string | null | undefined): boolean {
  return tierOf(role) === 'SUPER_ADMIN';
}

/**
 * Canonical authority-tier labels — the ONLY authority strings CRM 2.0 UI is
 * permitted to render (Req 13.1, 13.2, 13.4). Keyed by {@link AuthorityTier},
 * so every label is derived from {@link tierOf} and a raw backend role string
 * ("superadmin", "admin", "manager", ...) is never shown. CRM 2.0 views MUST
 * source authority labels from here (or {@link tierLabelOf}) rather than
 * defining their own role→label map.
 */
export const AUTHORITY_TIER_LABEL: Record<AuthorityTier, string> = {
  SUPER_ADMIN: 'Executive',
  ADMIN: 'Administrator',
  EMPLOYEE: 'Employee',
};

/**
 * Resolve a backend role directly to its authority-tier label
 * (`Executive` | `Administrator` | `Employee`). Thin convenience over
 * {@link tierOf} for CRM 2.0 views that only need the tier label. This is not a
 * new role→label mapping: roles are collapsed to a tier by {@link tierOf}, and
 * only the three tiers are labeled. Never returns a raw role string.
 */
export function tierLabelOf(role: Role | string | null | undefined): string {
  return AUTHORITY_TIER_LABEL[tierOf(role)];
}

interface IdentityInput {
  displayName?: string | null;
  role?: Role | string | null;
}

export interface ResolvedIdentity {
  tier: AuthorityTier;
  /** Short designation chip — safe to show anywhere (never an internal role). */
  designation: string;
  /** Full attribution label for "Assigned by / Created by / Approved by". */
  attribution: string;
}

/**
 * Build-time white-label configuration. Read defensively: the hermetic test
 * harness runs without Vite, so `import.meta.env` is undefined there.
 */
function envValue(key: string): string {
  try {
    const env = (import.meta as unknown as { env?: Record<string, string | undefined> }).env;
    return env?.[key]?.trim() ?? '';
  } catch {
    return '';
  }
}

/** Owner designation shown as the chip, e.g. "CEO & Founder". Never a raw role. */
export function ownerDesignation(): string {
  return envValue('VITE_OWNER_DESIGNATION') || 'Owner';
}

/**
 * Owner attribution. Deliberately independent of the signed-in user's record
 * (the executive is attributed as an office, not as a user row) — but the name
 * comes from deployment configuration, never a hardcoded person.
 * Configure with VITE_OWNER_NAME / VITE_OWNER_DESIGNATION.
 */
export function ownerAttribution(): string {
  const designation = ownerDesignation();
  const name = envValue('VITE_OWNER_NAME');
  return name ? `${name} — ${designation}` : designation;
}

/**
 * Resolve a user into tier-safe presentation labels.
 * - SUPER_ADMIN → the configured owner attribution (display name ignored by design)
 * - ADMIN       → "Department Administrator"
 * - EMPLOYEE    → the person's display name (or "Employee")
 */
export function identityOf(user: IdentityInput | null | undefined): ResolvedIdentity {
  const tier = tierOf(user?.role);
  if (tier === 'SUPER_ADMIN') {
    return { tier, designation: ownerDesignation(), attribution: ownerAttribution() };
  }
  if (tier === 'ADMIN') {
    return {
      tier,
      designation: 'Administrator',
      attribution: 'Department Administrator',
    };
  }
  return {
    tier,
    designation: 'Employee',
    attribution: user?.displayName?.trim() || 'Employee',
  };
}
